
import { init, RequestedBrowserTransport, LogLevel, EasyCallControlFactory, ILogger, IConfig, IApi, IDevice, DeviceId, ISingleCallControl, MuteState, IWebHidDevice } from '@gnaudio/jabra-js';

let jabraControl : JabraControl | null;

interface ISignalCallbacks {
    doWebHidMute: (instance: number, muted: boolean) => void,
    doWebHidAnswer: (instance: number) => void
    doWebHidHangup: (instance: number) => void
}

export async function CreateJabraControl(appId: string, appName : string, logLevel: string, signalCallbacks : ISignalCallbacks, eventCallbacks: JabraEventCallbacks) {
    const jabra = new JabraControl(signalCallbacks, eventCallbacks);
    jabraControl = null;
    try {
        jabraControl = await jabra.init(appId, appName, logLevel);
    } catch (error) {
        console.error("Failed to initialise Jabra API: ", error);
    }
    return jabraControl;
}



class JabraDevice {
    _signalCallbacks: ISignalCallbacks
    _device : IDevice | null | undefined;
    _jabraSingleCallControl : ISingleCallControl | null | undefined;
    _muteSubscription : any = null;
    _ringSubscription : any = null;
    _callSubscription : any = null;
    _muteState = 'no-ongoing-calls';    
    _callActive = false;   
    _ringState = false; 
    _externalCallState = 'idle';
    _externalIsMuted = false;
    _externalCallInstance = 0;
    _instanceStateMap = new Map<number, string>([
        [0, ''],
        [1, ''],
        [2, ''],
        [3, ''], 
    ]);




    constructor (device : IDevice, signalCallbacks : ISignalCallbacks) {
        this._device = device;
        this._signalCallbacks = signalCallbacks;
    }

    async activate(jabraSingleCallControl : ISingleCallControl) {
        this._jabraSingleCallControl = jabraSingleCallControl;

      
        // Subscribe to the muteState of the new device
        this._muteSubscription = this._jabraSingleCallControl.muteState.subscribe(
            (muteState: MuteState) => {
                console.log("Web HID mute state is ", muteState);
                this._muteState = muteState;
                if ((this._externalIsMuted && muteState !== 'muted') || (!this._externalIsMuted && muteState === 'muted')) {
                    if (this._externalCallState !== 'idle') {
                        try {
                            this._signalCallbacks?.doWebHidMute(this._externalCallInstance, muteState === 'muted');
                        } catch (error) {
                            console.warn("Exception when trying to mute call from web HID: ", error);
                        }
                    }
                }
            },
        );
      
        // Subscribe to the call state of the new device
        this._callSubscription = this._jabraSingleCallControl.callActive.subscribe(
            (callActive : boolean) => {
                console.log("Web HID call active ", callActive);
                this._callActive = callActive;
                if (!this._callActive && this._externalCallState === 'active') {
                    console.log("Hanging up web HID call");
                    try {
                        // If there are more than one call (XOR role) as indicated by provisionally maintained call state map
                        // then determine the most appropriate call to terminate
                        const callInstance = this.DetermineMostActiveCallInstanceForHangup(this._externalCallInstance);
                        if (callInstance !== this._externalCallInstance) {
                            console.log(`Changed call instance target for web HID hangup: [${this._externalCallInstance}]-->[${callInstance}]`);
                        }
                        this._signalCallbacks?.doWebHidHangup(callInstance)
                    } catch (error) {
                        console.warn("Exception when trying to answer call from web HID: ", error);
                    }
                }
            });
        this._ringSubscription = this._jabraSingleCallControl.ringState.subscribe(
            (ringState : boolean) => {
                console.log("Web HID call incoming alert ", ringState);
                this._ringState = ringState;
            });
        console.log("DEBUG: select: _externalCallState=", this._externalCallState);
        if (this._externalCallState === 'incoming') {
            console.log("Signalling incoming call on select");
            this.UpdateProvisionalCallState(this._externalCallState, this._externalCallInstance);
            await this.OnExternalCallUpdate(this._externalCallState, this._externalIsMuted, this._externalCallInstance);
        }
    }

    deactivate() {
        this._muteSubscription?.unsubscribe();
        this._muteSubscription = null;
        this._callSubscription?.unsubscribe();
        this._callSubscription = null;
        this._ringSubscription?.unsubscribe();
        this._ringSubscription = null;
        this._jabraSingleCallControl?.teardown();
        this._jabraSingleCallControl = null;
        this._muteState = 'no-ongoing-calls';
        this._ringState = false;
        this._callActive = false;
        this._ringState = false;
        this._externalCallState = 'idle';
        this._externalIsMuted = false;
        this._externalCallInstance = 0;    
    }

    UpdateProvisionalCallState(externalCallState : string, externalCallInstance : number) {
        this._instanceStateMap.set(externalCallInstance, externalCallState);
    }

    private DetermineMostActiveCallInstanceForHangup(defaultCallInstance: number) {
        // outgoing call is preferred with a min instance number        
        for (const entry of Array.from(this._instanceStateMap.entries())) {
            if (entry[1] === 'activeOutgoing') {
                console.log(`DetermineMostActiveCallInstanceForHangup(): the most active call instance ${entry[0]}:${entry[1]}`);
                return entry[0];
            }
        }
        // incoming call is preferred with a min instance number
        for (const entry of Array.from(this._instanceStateMap.entries())) {
            if (entry[1] === 'incoming') {
                console.log(`DetermineMostActiveCallInstanceForHangup(): the most active call instance ${entry[0]}:${entry[1]}`);
                return entry[0];
            }
        }
        // outgoing call is preferred with a max instance number (consultation call for XOR)
        for (const entry of Array.from(this._instanceStateMap.entries()).reverse()) {
            if (entry[1] === 'active') {
                console.log(`DetermineMostActiveCallInstanceForHangup(): the most active call instance ${entry[0]}:${entry[1]}`);
                return entry[0];
            }
        }
        return defaultCallInstance;
    }


    async OnExternalCallUpdate(externalCallState : string, externalIsMuted : boolean, externalCallInstance : number) {
        const oldExternalCallState = this._externalCallState;
        const oldexternalIsMuted = this._externalIsMuted;
        this._externalCallState = externalCallState;
        this._externalIsMuted = externalIsMuted;
        this._externalCallInstance = externalCallInstance;
        if (!this._jabraSingleCallControl) {
            throw new Error("OnExternalCallUpdate(): call control is unavailable");
        }
        if (oldexternalIsMuted !== externalIsMuted) {
            if (externalIsMuted && this._muteState === 'unmuted') {
                try {
                    await this._jabraSingleCallControl.mute();
                } catch (error) {
                    console.error("Failed to mute web HID");
                }
            }
            else if (!externalIsMuted && this._muteState === 'muted') {
                try {
                    await this._jabraSingleCallControl.unmute();
                } catch (error) {
                    console.error("Failed to unmute web HID");
                }
            }
        }
        if (oldExternalCallState !== externalCallState)    {
            switch (externalCallState) {
                case 'incoming':
                    console.log("Signalling incoming web HID call");
                    const accepted = await this._jabraSingleCallControl.signalIncomingCall(60000);
                    if (accepted) {
                        console.log("Accepting web HID call");
                        try {
                            this._signalCallbacks?.doWebHidAnswer(this._externalCallInstance);
                        } catch (error) {
                            console.warn("Exception when trying to answer call from web HID: ", error);
                            try {
                                await this._jabraSingleCallControl.endCall();
                            } catch (error1) {
                                console.warn("Exception when trying to end web HID call: ", error1);
                            }
                        }
                    } else {
                        console.log("Rejecting web HID call");
                        try {
                            this._signalCallbacks?.doWebHidHangup(this._externalCallInstance);
                        } catch (error) {
                            console.warn("Exception when trying to answer call from web HID: ", error);
                        }
                    }
                    break;
                case 'active':
                    if (this._ringState) {
                        console.log("Accepting Web HID incoming call");
                        await this._jabraSingleCallControl.acceptIncomingCall();
                    }    
                    if (!this._callActive) {
                        console.log("Starting Web HID active call");
                        await this._jabraSingleCallControl.startCall();
                    }
                    break;
                case 'idle':
                    if (this._ringState) {
                        console.log("Ending incoming Web HID call");
                        this._jabraSingleCallControl.rejectIncomingCall();
                    }
                    if (this._callActive) {
                        console.log("Ending active Web HID call");
                        await this._jabraSingleCallControl.endCall();
                    } 
                    break;
            }
        }
    }
    

}

export type JabraEventCallbacks = {
    onDeviceSelected?: (deviceName: string, deviceId: string) => void,
}

export class JabraControl {
    _signalCallbacks : any = {};
    _eventCallbacks : JabraEventCallbacks = {};
    _jabraApi: IApi | null | undefined;
    _jabraDevices: JabraDevice[] = [];
    _currentDeviceId : DeviceId | null | undefined;
    _externalCallState = 'idle';
    _externalIsMuted = false;
    _externalCallInstance = 0;
    _deviceListSub: any;
    _deviceAddedSub: any;
    _deviceRemovedSub: any;


    constructor (signalCallbacks : any, eventCallbacks : JabraEventCallbacks) {
        this._signalCallbacks = signalCallbacks;
        this._eventCallbacks = eventCallbacks;
    }

    async init(appID: string, appName: string, logLevel: string) {
        let jabraLogLevel: LogLevel | null | undefined;
        if (logLevel === 'error') jabraLogLevel = LogLevel.ERROR;
        else if (logLevel === 'warning') jabraLogLevel = LogLevel.WARNING;
        else if (logLevel === 'info') jabraLogLevel = LogLevel.INFO;
        const config: IConfig = {
            // logger: {
            //     write (logEvent) {             
            //         switch (jabraLogLevel)   {
            //             // @ts-expect-error
            //             case LogLevel.INFO:
            //                 if (logEvent.level === LogLevel.INFO) console.log("Web HID log event: ", logEvent.message, logEvent.layer);
            //                 /* FALLTHROUGH */
            //             // @ts-expect-error
            //             case LogLevel.WARNING:
            //                 if (logEvent.level === LogLevel.WARNING) console.warn("Web HID log event: ", logEvent.message, logEvent.layer);
            //                 /* FALLTHROUGH */
            //             case LogLevel.ERROR:
            //                 if (logEvent.level === LogLevel.ERROR) console.error("Web HID log event: ", logEvent.message, logEvent.layer);
            //         }
            //     }
            // },
            partnerKey: '3779-76d71cd3-4cbb-4622-92e1-0281b596f8e9',
            appId: appID,
            appName: appName,
            transport: RequestedBrowserTransport.WEB_HID,
        };

        this._jabraApi = undefined;
        this._jabraApi = await init(config);
        console.log("Jabra API initialised");
        this._deviceListSub?.unsubscribe();
        this._deviceListSub = this._jabraApi.deviceList.subscribe((devices: IDevice[]) => {
            devices.forEach(async (device : IDevice) => {
              console.log("Current devices: ", device.name, "-", device.productId, "-", device.type);
            });
        });
        this._deviceAddedSub?.unsubscribe();
        this._deviceAddedSub = this._jabraApi.deviceAdded.subscribe(async (device : IDevice) => {
            try {
                await this.AddDevice(device);
            } catch (error) {
                console.error("Failed to add device [", device.id, "][", device.name, "-", device.productId, "-", device.type, "]: ", error);
            }
        });    
        this._deviceRemovedSub?.unsubscribe();
        this._deviceRemovedSub = this._jabraApi.deviceRemoved.subscribe(async (device : IDevice) => {
            try {
                this.RemoveDevice(device);
            } catch (error) {
                console.error("Failed to remove device [", device.id, "][", device.name, "-", device.productId, "-", device.type, "]: ", error);
            }
        });    
        return this;
    }

    shutdown() {
        this._deviceListSub?.unsubscribe();
        this._deviceAddedSub?.unsubscribe();
        this._deviceRemovedSub?.unsubscribe();
        this._deviceListSub = null;
        this._deviceAddedSub = null;
        this._deviceRemovedSub = null;
        if (this._currentDeviceId) {
            const currentDevice = this._jabraDevices.find((d) => d._device && this._currentDeviceId && d._device.id.equals(this._currentDeviceId));
            if (currentDevice) {                
                currentDevice.deactivate();
            }
            this._currentDeviceId = null;
        }
        this._jabraDevices = [];
    }

    async OnExternalCallUpdate(externalCallState : string, externalIsMuted : boolean, externalCallInstance: number) {
        console.log("OnExternalCallUpdate(): externalIsMuted=", externalIsMuted, "this._externalIsMuted=", this._externalIsMuted);
        if (this._externalCallState === externalCallState && this._externalCallInstance === externalCallInstance && this._externalIsMuted === externalIsMuted) return;
        if (this._currentDeviceId) {
            const currentDevice = this._jabraDevices.find((d) => d._device && this._currentDeviceId && d._device.id.equals(this._currentDeviceId));
            if (currentDevice) {
                currentDevice.UpdateProvisionalCallState(externalCallState, externalCallInstance);
                if (this._externalCallInstance !== externalCallInstance && this._externalCallState !== 'idle') return; // one call at a time
                this._externalCallState = externalCallState;
                this._externalIsMuted = externalIsMuted;
                this._externalCallInstance = externalCallInstance;
                await currentDevice.OnExternalCallUpdate(externalCallState, externalIsMuted, externalCallInstance);
            } else {
                console.warn("Current device id [", this._currentDeviceId.toString() , "] doesn't have matching device in the list");
            }
        } 
    }

    async AddDevice(device: IDevice) {
        if (device.vendorId !== 2830) {
            console.warn("Device [", device.id, "] [", device.name, "-", device.productId, "-", device.type, "][" + device.vendorId + "] vendor ID is not Jabra");
            return;
        }
        if (this._jabraDevices.find((d) => d._device && d._device.id.equals(device.id))) {
            console.warn("Device [", device.id, "] [", device.name, "-", device.productId, "-", device.type, "][" + device.vendorId + "] already added. Not added again.");
            return;
        }
        const easyCallControlFactory = this._jabraApi ? new EasyCallControlFactory(this._jabraApi) : null;
        if (easyCallControlFactory && !easyCallControlFactory.supportsEasyCallControl) {
            console.warn("Device [", device.id, "] [", device.name, "-", device.productId, "-", device.type, "][" + device.vendorId + "] doesn't support easy call control. Not added.");
            return;
        }

        const jabraDevice = new JabraDevice(device, this._signalCallbacks);
        this._jabraDevices.push(jabraDevice);
        console.log("Device added: [", device.id, "][", device.name, "-", device.productId, "-", device.type, "][" + device.vendorId + "]");
        this._jabraDevices.forEach((d) => console.log("Remaining device: [", d._device ? d._device.id : null, "][", d._device ? d._device.name : null, "-", d._device ? d._device.productId : null, "-", d._device ? d._device.type : 0, "]"));
    }

    RemoveDevice(device: IDevice) {
        if (this._jabraDevices.findIndex((d) => d._device && d._device.id.equals(device.id)) < 0) {
            console.warn("Device [", device.id, "] [", device.name, "-", device.productId, "-", device.type, "] already removed");
            return;
        }
        // deactivate current device control
        if (this._currentDeviceId && this._currentDeviceId.equals(device.id)) {
            const currentDevice = this._jabraDevices.find((d) => d._device && this._currentDeviceId && d._device.id.equals(this._currentDeviceId));
            if (!currentDevice) {
                console.warn("Current device Id [", this._currentDeviceId.id.toString() ,"] not found");
            } else {
                currentDevice.deactivate();
            }
            this._currentDeviceId = null;
        }
        // remove
        this._jabraDevices = this._jabraDevices.filter((d) => !d._device || !d._device.id.equals(device.id));
        console.log("Device removed: [", device.id, "][", device.name, "-", device.productId, "-", device.type, "]");
        this._jabraDevices.forEach((d) => console.log("Remaining device: [", d._device ? d._device.id : null, "][", d._device ? d._device.name : null, "-", d._device ? d._device.productId : null, "-", d._device ? d._device.type : 0, "]"));
    }

    Deselect() {
        if (this._currentDeviceId) {
            const currentDevice = this._jabraDevices.find((d) => d._device && this._currentDeviceId && d._device.id.equals(this._currentDeviceId));
            if (currentDevice) {                
                currentDevice.deactivate();
            }
            this._currentDeviceId = null;
        }
    }


    async SelectByWebHidDevice(deviceId?: string) {
        let selectedDevice;
        if (deviceId) {
            selectedDevice = this._jabraDevices.find((d) => d._device && d._device.id.id === deviceId);
            if (!selectedDevice) {
                throw new Error("Device matching id [" + deviceId  + "] not found");
            }
        } else if (this._jabraDevices.length > 0) {
            selectedDevice = this._jabraDevices[0];
        }
        if (!selectedDevice || !selectedDevice._device) {
            console.log("No web HID device selected");
            return;
        }

        if (this._currentDeviceId) {
            const currentDevice = this._jabraDevices.find((d) => d._device && this._currentDeviceId && d._device.id.equals(this._currentDeviceId));
            if (!currentDevice) {
                throw new Error("Current device Id [" +  this._currentDeviceId.id.toString() + "] not found");
            }
            await currentDevice.deactivate();
        }
        const easyCallControlFactory = this._jabraApi ? new EasyCallControlFactory(this._jabraApi) : null;
        const callControl = 
            await easyCallControlFactory?.createSingleCallControl(
                selectedDevice._device, 
                {callActive: this._externalCallState === 'active', muted: this._externalIsMuted});
        if (!callControl) return;
        selectedDevice.deactivate();
        selectedDevice._externalCallState = this._externalCallState;
        selectedDevice._externalIsMuted = this._externalIsMuted;
        selectedDevice._externalCallInstance = this._externalCallInstance;
        selectedDevice._instanceStateMap.set(this._externalCallInstance, this._externalCallState);
        await selectedDevice.activate(callControl);
        this._currentDeviceId = selectedDevice._device.id;
        console.log(`Selected call control device [${selectedDevice._device.name}] with id [${selectedDevice._device.id.id}]`);
        this._eventCallbacks.onDeviceSelected?.(selectedDevice._device.name, selectedDevice._device.id.id)
    }

    GetDeviceList()  {
        let deviceList : {deviceId: string, deviceName: string, serialNumber: string} [] = [];
        this._jabraDevices.forEach(d => { if (d._device) deviceList.push({
            deviceId: d._device.id.id,
            deviceName: d._device.name,
            serialNumber: d._device.serialNumber,
        })});
        return deviceList;
    }
}


